import React, { useContext, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import BdsButton from '../../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import EmptyCard from '../../../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import GenericMenu from '../../../../../../shared/components/v3/GenericMenu/GenericMenu';
import { CHEVRON_DOWN_V3 } from '../../../../../../shared/constants/images';
import { PlansContext } from '../../../../../../shared/context/plans/plansContext';
import { IMemberFRHistoryYear } from '../../../../../../shared/hooks/useCollaboratorConsumptions/useCollaboratorConsumptions';
import { IMemberFRPeriodConsumptions } from '../../../../../../shared/interfaces/collaborators';
import { TRANSLATION_COLLABORATORS } from '../../../../../../shared/translations/collaborators';
import { TRANSLATIONS_GLOBAL } from '../../../../../../shared/translations/commons';
import { capitalize, getLocalizedMonthName } from '../../../../../../shared/utils/utils';
import ConsumptionTable from '../ConsumptionTable/ConsumptionTable';
import PeriodChips, { IPeriodChip } from '../PeriodChips/PeriodChips';

import './HistoryCard.scss';

export const TOTAL_PERIOD_ID = 'total';
export const MIGRATED_PERIOD_ID = 'migrated';

interface IHistoryCard {
  history: {
    data: IMemberFRHistoryYear[];
    loading: boolean;
    error: boolean;
  };
  onRetry: () => void;
}

const HistoryCard = ({ history, onRetry }: IHistoryCard) => {
  const { t } = useTranslation();
  const { historyCard, empty } = TRANSLATION_COLLABORATORS.detail.collaboratorBalanceRF;
  const { modelType } = useContext(PlansContext);
  const isForecast = modelType === 'forecast';
  const titleByModel: Record<string, string> = {
    pocket: historyCard.titlePocket,
    forecast: historyCard.titleForecast
  };
  const title = t(titleByModel[modelType] ?? historyCard.title);

  const years = useMemo(() => history.data.map(({ year }) => year), [history.data]);
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
  const [selectedPeriodId, setSelectedPeriodId] = useState(TOTAL_PERIOD_ID);
  const [showYearMenu, setShowYearMenu] = useState(false);

  const activeYear = selectedYear !== undefined && years.includes(selectedYear) ? selectedYear : years[0];
  const yearData = history.data.find(({ year }) => year === activeYear);
  const periods: IPeriodChip[] = useMemo(() => {
    if (!yearData) return [];
    const cycles = [...yearData.cycles].sort((a, b) => (a.cycleMonth ?? 0) - (b.cycleMonth ?? 0));
    return [
      { id: TOTAL_PERIOD_ID, label: t(historyCard.total) },
      ...(yearData.migrated ? [{ id: MIGRATED_PERIOD_ID, label: t(historyCard.migrated) }] : []),
      ...cycles.map(({ payrollId, cycleMonth }) => ({
        id: payrollId,
        label: cycleMonth ? capitalize(getLocalizedMonthName(cycleMonth)) : ''
      }))
    ];
  }, [yearData, t, historyCard.total, historyCard.migrated]);

  const getActivePeriod = (): IMemberFRPeriodConsumptions | undefined => {
    if (selectedPeriodId === TOTAL_PERIOD_ID) return yearData?.total;
    if (selectedPeriodId === MIGRATED_PERIOD_ID) return yearData?.migrated;
    return yearData?.cycles.find(({ payrollId }) => payrollId === selectedPeriodId);
  };
  const activePeriod = getActivePeriod();

  const changeYear = (year: string) => {
    setShowYearMenu(false);
    const picked = Number(year);
    setSelectedYear(years.includes(picked) ? picked : years[0]);
    setSelectedPeriodId(TOTAL_PERIOD_ID);
  };

  const renderContent = () => {
    const hasConsumption =
      activePeriod !== undefined &&
      activePeriod.hasData &&
      activePeriod.consumptions.some(({ exempt, nonExempt, total }) => exempt + nonExempt + total !== 0);

    if (!activePeriod || !hasConsumption) {
      return <EmptyCard primaryText={t(empty.title)} secondaryText={t(empty.description)} />;
    }

    return (
      <ConsumptionTable
        rows={activePeriod.consumptions}
        totalExempt={activePeriod.totalExempt}
        totalNonExempt={activePeriod.totalNonExempt}
        total={activePeriod.total}
        currency={activePeriod.currency}
      />
    );
  };

  const header = (
    <div className="top flex flex-col w-full">
      <div className="title">{title}</div>
      {isForecast && <div className="note">{t(historyCard.forecastNote)}</div>}
    </div>
  );

  if (history.loading) {
    return (
      <div className="history-card flex flex-col w-full" data-testid="history-card">
        {header}
        <Skeleton className="collaborator-balance-skeleton" variant="rectangular" height={120} />
      </div>
    );
  }

  return (
    <div className="history-card flex flex-col w-full" data-testid="history-card">
      {header}
      {history.error ? (
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          action={onRetry}
        />
      ) : (
        <div className="history-card-body flex flex-col w-full">
          <div className="history-card-filters flex flex-row items-center w-full">
            {periods.length > 1 && (
              <PeriodChips
                periods={periods}
                selectedId={selectedPeriodId}
                onSelect={setSelectedPeriodId}
                ariaLabel={title}
              />
            )}
            {years.length > 0 && (
              <div className="history-card-year">
                <BdsButton
                  action={() => setShowYearMenu(!showYearMenu)}
                  variant="secondary"
                  size="s"
                  iconRight={CHEVRON_DOWN_V3}
                >
                  {String(activeYear)}
                </BdsButton>
                <GenericMenu
                  setShowGenericMenu={setShowYearMenu}
                  showGenericMenu={showYearMenu}
                  title={t(historyCard.year)}
                  type="custom"
                  className="history-card-year-menu"
                  customData={{
                    showButtonFilter: years.length > 1,
                    data: years.map((year) => ({ name: String(year), value: String(year) })),
                    onChanged: changeYear,
                    value: String(activeYear)
                  }}
                />
              </div>
            )}
          </div>
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default HistoryCard;
